import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from './DataTable';
import Loader from './Loader';

const API_URL = '/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'User',
    canViewPricing: true,
    canManageAssets: true,
    canManageEmployees: true,
    canManageAssignments: true,
    canManageRepairs: true,
    canManageUsers: false,
    canViewReports: true
  });

  useEffect(() => {
    fetchUsers();
    document.title = 'User Management - Saral Asset Manager';
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/users/${editingId}`, formData);
      } else {
        await axios.post(`${API_URL}/users`, formData);
      }
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      alert(error.response?.data || 'Error saving user');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      username: '',
      password: '',
      role: 'User',
      canViewPricing: true,
      canManageAssets: true,
      canManageEmployees: true,
      canManageAssignments: true,
      canManageRepairs: true,
      canManageUsers: false,
      canViewReports: true
    });
    setEditingId(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    if (user.username === 'admin') {
      alert('Admin user cannot be modified');
      return;
    }
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      canViewPricing: user.canViewPricing,
      canManageAssets: user.canManageAssets,
      canManageEmployees: user.canManageEmployees,
      canManageAssignments: user.canManageAssignments,
      canManageRepairs: user.canManageRepairs,
      canManageUsers: user.canManageUsers,
      canViewReports: user.canViewReports
    });
    setEditingId(user.id);
    setShowModal(true);
  };

  const handleDelete = async (id, user) => {
    if (user.username === 'admin') {
      alert('Admin user cannot be deleted');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_URL}/users/${id}`);
        fetchUsers();
      } catch (error) {
        alert('Error deleting user');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const columns = [
    { field: 'username', label: 'Username' },
    { 
      field: 'role', 
      label: 'Role',
      render: (value) => (
        <span className={`badge ${value === 'Admin' ? 'bg-danger' : 'bg-primary'}`}>
          {value}
        </span>
      )
    },
    { 
      field: 'permissions', 
      label: 'Permissions',
      render: (value, item) => (
        <div className="d-flex flex-wrap gap-1">
          {item.canViewPricing && <span className="badge bg-info">Pricing</span>}
          {item.canManageAssets && <span className="badge bg-success">Assets</span>}
          {item.canManageEmployees && <span className="badge bg-warning">Employees</span>}
          {item.canManageAssignments && <span className="badge bg-primary">Assignments</span>}
          {item.canManageRepairs && <span className="badge bg-secondary">Repairs</span>}
          {item.canManageUsers && <span className="badge bg-danger">Users</span>}
        </div>
      )
    },
    { 
      field: 'isActive', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      field: 'createdDate', 
      label: 'Created',
      render: (value) => new Date(value).toLocaleDateString()
    }
  ];

  return (
    <div className="container-fluid">
      <div className="page-header">
        <h1 className="page-title mb-0">User Management</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1"></i>Add User
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="table-loader">
              <Loader text="Loading users..." size="medium" />
            </div>
          ) : (
            <DataTable
              data={users}
              columns={columns}
              onEdit={handleEdit}
              onDelete={handleDelete}
              hideActions={(item) => item.username === 'admin'}
            />
          )}
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-user me-2"></i>
                {editingId ? 'Edit User' : 'Add New User'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      disabled={editingId}
                    />
                  </div>
                  {!editingId && (
                    <div className="col-md-6">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      style={{
                        backgroundColor: document.body.getAttribute('data-theme') === 'dark' ? '#2a2a2a' : '#ffffff',
                        color: document.body.getAttribute('data-theme') === 'dark' ? '#e0e0e0' : '#495057',
                        border: document.body.getAttribute('data-theme') === 'dark' ? '1px solid #404040' : '1px solid #ced4da'
                      }}
                    >
                      <option value="User">User</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                </div>
                
                <hr className="my-4" />
                <h6>Permissions</h6>
                
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canViewPricing}
                        onChange={(e) => setFormData({ ...formData, canViewPricing: e.target.checked })}
                      />
                      <label className="form-check-label">View Pricing</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canManageAssets}
                        onChange={(e) => setFormData({ ...formData, canManageAssets: e.target.checked })}
                      />
                      <label className="form-check-label">Manage Assets</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canManageEmployees}
                        onChange={(e) => setFormData({ ...formData, canManageEmployees: e.target.checked })}
                      />
                      <label className="form-check-label">Manage Employees</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canManageAssignments}
                        onChange={(e) => setFormData({ ...formData, canManageAssignments: e.target.checked })}
                      />
                      <label className="form-check-label">Manage Assignments</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canManageRepairs}
                        onChange={(e) => setFormData({ ...formData, canManageRepairs: e.target.checked })}
                      />
                      <label className="form-check-label">Manage Repairs</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.canManageUsers}
                        onChange={(e) => setFormData({ ...formData, canManageUsers: e.target.checked })}
                      />
                      <label className="form-check-label">Manage Users</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                  {saving ? <Loader size="small" /> : (editingId ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
